import { type HalftoneGrid } from './agents-bird.halftone';

export type DotSet = { x: number[]; y: number[]; r: number[] };

export type Blob = { x: number; y: number; radius: number; tone: number };
type Form = 'bird' | 'dots' | 'ring' | 'cloud';
type Step = { form: Form; holdMs: number; morphMs: number };
export type DotMorph = (progress: number) => DotSet;

export const THINKING_EXIT_MS = 950;

const BLOB_MORPH_MS = 800;
const STAGGER = 0.12;
const DOT_GAIN = 0.72;
const MIN_RADIUS = 0.3;
const DOTS_WAVE_MS = 1400;
const RING_CLUSTERS = 6;
const RING_RADIUS = 0.21;
const RING_SPIN_MS = 1600;
const CLOUD_BREATH_MS = 1100;

const STEPS: Step[] = [
  { form: 'bird', holdMs: 1000, morphMs: THINKING_EXIT_MS },
  { form: 'dots', holdMs: 2000, morphMs: BLOB_MORPH_MS },
  { form: 'ring', holdMs: 2200, morphMs: BLOB_MORPH_MS },
  { form: 'cloud', holdMs: 2000, morphMs: THINKING_EXIT_MS },
];

const CYCLE_MS = STEPS.reduce(
  (sum, step) => sum + step.holdMs + step.morphMs,
  0
);

const CLOUD_PUFFS = [
  { x: 0.44, y: 0.4, radius: 0.17 },
  { x: 0.6, y: 0.36, radius: 0.15 },
  { x: 0.32, y: 0.47, radius: 0.12 },
  { x: 0.53, y: 0.5, radius: 0.14 },
  { x: 0.7, y: 0.47, radius: 0.11 },
  { x: 0.3, y: 0.7, radius: 0.055 },
  { x: 0.2, y: 0.82, radius: 0.035 },
];

const LIGHT = (() => {
  const length = Math.hypot(0.55, -0.65, 0.5);
  return { x: 0.55 / length, y: -0.65 / length, z: 0.5 / length };
})();

const easeInOut = (t: number) =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

const clampUnit = (value: number) => Math.min(1, Math.max(0, value));

const jitter = (index: number) => {
  const value = Math.sin(index * 12.9898) * 43758.5453;
  return value - Math.floor(value);
};

const dotBlobs = (local: number): Blob[] =>
  [0.3, 0.5, 0.7].map((x, index) => {
    const lift = Math.max(
      0,
      Math.sin((local / DOTS_WAVE_MS) * Math.PI * 2 - index * 0.9)
    );
    return { x, y: 0.5 - 0.05 * lift, radius: 0.085, tone: 0.55 + 0.4 * lift };
  });

const ringBlobs = (elapsed: number): Blob[] => {
  const spin = elapsed / RING_SPIN_MS;
  return Array.from({ length: RING_CLUSTERS }, (_, index) => {
    const angle = (index / RING_CLUSTERS) * Math.PI * 2 - Math.PI / 2;
    const tail = (((index / RING_CLUSTERS - spin) % 1) + 1) % 1;
    return {
      x: 0.5 + Math.cos(angle) * RING_RADIUS,
      y: 0.5 + Math.sin(angle) * RING_RADIUS,
      radius: 0.045 + 0.03 * tail,
      tone: 0.35 + 0.6 * tail,
    };
  });
};

const cloudBlobs = (local: number): Blob[] => {
  const grow = 1 + 0.06 * Math.sin((local / CLOUD_BREATH_MS) * Math.PI);
  return CLOUD_PUFFS.map((puff, index) => ({
    ...puff,
    radius: puff.radius * grow,
    tone: 0.4 + 0.12 * Math.sin(local / 500 + index),
  }));
};

const blobsFor = (form: Form, local: number, elapsed: number) =>
  form === 'dots'
    ? dotBlobs(local)
    : form === 'ring'
      ? ringBlobs(elapsed)
      : cloudBlobs(local);

const nearestIndex = (point: { x: number; y: number }, blobs: Blob[]) => {
  let nearest = 0;
  let nearestDistance = Infinity;
  blobs.forEach((blob, index) => {
    const distance = (blob.x - point.x) ** 2 + (blob.y - point.y) ** 2;
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = index;
    }
  });
  return nearest;
};

const lerpBlob = (from: Blob, to: Blob, t: number): Blob => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
  radius: from.radius + (to.radius - from.radius) * t,
  tone: from.tone + (to.tone - from.tone) * t,
});

const blendBlobs = (from: Blob[], to: Blob[], progress: number) => {
  const usedSources = new Set<number>();
  const arriving = to.map((target, index) => {
    const sourceIndex = nearestIndex(target, from);
    usedSources.add(sourceIndex);
    const t = easeInOut(clampUnit((progress - jitter(index) * 0.2) / 0.8));
    return lerpBlob(from[sourceIndex], target, t);
  });
  const t = easeInOut(clampUnit(progress / 0.8));
  const merging = from
    .filter((_, index) => !usedSources.has(index))
    .map((source) =>
      lerpBlob(source, { ...to[nearestIndex(source, to)], radius: 0 }, t)
    );
  return [...arriving, ...merging];
};

export const blobDots = (
  blobs: Blob[],
  pixels: number,
  pitch: number,
  dark: boolean
): DotSet => {
  const dots: DotSet = { x: [], y: [], r: [] };
  const drawable = blobs.filter((blob) => blob.radius * pixels >= pitch * 0.9);
  const valueAt = (x: number, y: number) => {
    let height = 0;
    let owner: Blob | null = null;
    for (const blob of drawable) {
      const radius = blob.radius * pixels;
      const distanceSquared =
        ((x - blob.x * pixels) ** 2 + (y - blob.y * pixels) ** 2) /
        (radius * radius);
      if (distanceSquared >= 1) continue;
      const blobHeight = Math.sqrt(1 - distanceSquared) * radius;
      if (blobHeight > height) {
        height = blobHeight;
        owner = blob;
      }
    }
    if (!owner) return null;
    const radius = owner.radius * pixels;
    const nx = (x - owner.x * pixels) / radius;
    const ny = (y - owner.y * pixels) / radius;
    const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
    const diffuse = Math.max(0, nx * LIGHT.x + ny * LIGHT.y + nz * LIGHT.z);
    const shade = dark
      ? 0.1 + 0.75 * diffuse
      : Math.min(0.85, 0.12 + (1 - diffuse) ** 1.4);
    const rim = Math.min(1, nz * 2.2);
    return Math.max(0.06, shade * (0.55 + 0.6 * owner.tone) * rim);
  };
  const step = pitch / Math.SQRT2;
  for (let row = 0; row * step < pixels + step; row++) {
    for (let x = (row % 2) * step; x < pixels + step; x += step * 2) {
      const value = valueAt(x, row * step);
      if (value === null) continue;
      dots.x.push(x);
      dots.y.push(row * step);
      dots.r.push(pitch * DOT_GAIN * Math.sqrt(Math.min(1, value)));
    }
  }
  for (const blob of blobs) {
    const radius = blob.radius * pixels;
    if (radius >= pitch * 0.9 || radius < 0.25) continue;
    dots.x.push(blob.x * pixels);
    dots.y.push(blob.y * pixels);
    dots.r.push(
      Math.min(
        pitch * DOT_GAIN * Math.sqrt(0.4 + 0.5 * blob.tone),
        radius * 1.1
      )
    );
  }
  return dots;
};

const normalizedPositions = (dots: DotSet) => {
  const minX = Math.min(...dots.x);
  const maxX = Math.max(...dots.x);
  const minY = Math.min(...dots.y);
  const maxY = Math.max(...dots.y);
  const span = Math.max(maxX - minX, maxY - minY, 1e-6);
  const centreX = (minX + maxX) / 2;
  const centreY = (minY + maxY) / 2;
  return {
    u: dots.x.map((x) => (x - centreX) / span),
    v: dots.y.map((y) => (y - centreY) / span),
  };
};

const pairDots = (small: DotSet, large: DotSet) => {
  const from = normalizedPositions(small);
  const to = normalizedPositions(large);
  const taken = new Uint8Array(large.x.length);
  const pairs = new Int32Array(small.x.length);
  const order = small.x
    .map((_, index) => index)
    .sort((a, b) => jitter(a) - jitter(b));
  for (const index of order) {
    let nearest = 0;
    let nearestDistance = Infinity;
    for (let candidate = 0; candidate < large.x.length; candidate++) {
      if (taken[candidate]) continue;
      const distance =
        (from.u[index] - to.u[candidate]) ** 2 +
        (from.v[index] - to.v[candidate]) ** 2;
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = candidate;
      }
    }
    taken[nearest] = 1;
    pairs[index] = nearest;
  }
  return pairs;
};

export const createDotMorph = (from: DotSet, to: DotSet): DotMorph => {
  if (from.x.length === 0 || to.x.length === 0) {
    return (progress) => (progress < 0.5 ? from : to);
  }
  const growing = to.x.length >= from.x.length;
  const small = growing ? from : to;
  const large = growing ? to : from;
  const pairs = pairDots(small, large);
  const matched = new Uint8Array(large.x.length);
  pairs.forEach((index) => (matched[index] = 1));
  return (progress) => {
    const dots: DotSet = { x: [], y: [], r: [] };
    for (let index = 0; index < small.x.length; index++) {
      const partner = pairs[index];
      const t = easeInOut(
        clampUnit((progress - jitter(index) * STAGGER) / (1 - STAGGER))
      );
      const amount = growing ? t : 1 - t;
      dots.x.push(
        small.x[index] + (large.x[partner] - small.x[index]) * amount
      );
      dots.y.push(
        small.y[index] + (large.y[partner] - small.y[index]) * amount
      );
      dots.r.push(
        small.r[index] + (large.r[partner] - small.r[index]) * amount
      );
    }
    for (let index = 0; index < large.x.length; index++) {
      if (matched[index]) continue;
      const reveal = growing
        ? (progress - 0.3 - jitter(index) * 0.25) / 0.45
        : 1 - (progress - jitter(index) * 0.25) / 0.45;
      const amount = easeInOut(clampUnit(reveal));
      if (amount <= 0) continue;
      dots.x.push(large.x[index]);
      dots.y.push(large.y[index]);
      dots.r.push(large.r[index] * amount);
    }
    return dots;
  };
};

export const visibleDots = (grid: HalftoneGrid, radii: Float32Array) => {
  const dots: DotSet = { x: [], y: [], r: [] };
  for (let index = 0; index < radii.length; index++) {
    if (radii[index] < MIN_RADIUS) continue;
    dots.x.push(grid.x[index]);
    dots.y.push(grid.y[index]);
    dots.r.push(radii[index]);
  }
  return dots;
};

export const waveDots = (
  dots: DotSet,
  origin: { x: number; y: number },
  front: number,
  strength: number,
  thickness: number
): DotSet => ({
  x: dots.x,
  y: dots.y,
  r: dots.r.map((radius, index) => {
    const distance = Math.hypot(
      dots.x[index] - origin.x,
      dots.y[index] - origin.y
    );
    const wave = Math.exp(-(((distance - front) / thickness) ** 2));
    return radius * (1 + strength * wave);
  }),
});

export const drawDots = (
  context: CanvasRenderingContext2D,
  dots: DotSet,
  color: string
) => {
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  context.fillStyle = color;
  context.beginPath();
  for (let index = 0; index < dots.x.length; index++) {
    context.moveTo(dots.x[index] + dots.r[index], dots.y[index]);
    context.arc(dots.x[index], dots.y[index], dots.r[index], 0, Math.PI * 2);
  }
  context.fill();
};

export const stillThinkingDots = (
  pixels: number,
  pitch: number,
  dark: boolean
) => blobDots(dotBlobs(0), pixels, pitch, dark);

export const thinkingScene = (
  bird: DotSet,
  pixels: number,
  pitch: number,
  dark: boolean
) => {
  const morphs = new Map<number, DotMorph>();
  const formDots = (form: Form, local: number, elapsed: number) =>
    form === 'bird'
      ? bird
      : blobDots(blobsFor(form, local, elapsed), pixels, pitch, dark);
  return (elapsed: number): { dots: DotSet; isBird: boolean } => {
    let local = elapsed % CYCLE_MS;
    for (let index = 0; index < STEPS.length; index++) {
      const step = STEPS[index];
      const next = STEPS[(index + 1) % STEPS.length];
      if (local < step.holdMs) {
        return {
          dots: formDots(step.form, local, elapsed),
          isBird: step.form === 'bird',
        };
      }
      local -= step.holdMs;
      if (local < step.morphMs) {
        const progress = local / step.morphMs;
        if (step.form !== 'bird' && next.form !== 'bird') {
          const blobs = blendBlobs(
            blobsFor(step.form, step.holdMs, elapsed),
            blobsFor(next.form, 0, elapsed),
            progress
          );
          return { dots: blobDots(blobs, pixels, pitch, dark), isBird: false };
        }
        let morph = morphs.get(index);
        if (!morph) {
          morph = createDotMorph(
            formDots(step.form, step.holdMs, elapsed),
            formDots(next.form, 0, elapsed)
          );
          morphs.set(index, morph);
        }
        return { dots: morph(progress), isBird: false };
      }
      local -= step.morphMs;
    }
    return { dots: bird, isBird: true };
  };
};
